import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, MessageCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ScrapeHealthPanel from './ScrapeHealthPanel';
import type { ForumBase } from '../../types/forum';

interface DashboardStats {
  totalForums: number;
  totalActiveForums: number;
  totalCategories: number;
  totalPosts: number;
  activeForumPercentage: number;
  monthlyChanges: {
    forums: number;
    categories: number;
    posts: number;
    active: number;
  };
  topForums: Array<{
    name: string;
    score: number;
  }>;
  categoryDistribution: Array<{
    name: string;
    count: number;
  }>;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalForums: 0,
    totalActiveForums: 0,
    totalCategories: 0,
    totalPosts: 0,
    activeForumPercentage: 0,
    monthlyChanges: {
      forums: 0,
      categories: 0,
      posts: 0,
      active: 0
    },
    topForums: [],
    categoryDistribution: []
  });
  const [loading, setLoading] = useState(true);
  const [allForums, setAllForums] = useState<ForumBase[]>([]);

  useEffect(() => {
    fetchDashboardStats();
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchDashboardStats, 300000);
    return () => clearInterval(interval);
  }, []);

  const calculateForumScore = (topics: number, posts: number, members: number, foundedYear: number): number => {
    const topicsScore = (topics || 0) * 5;
    const postsScore = (posts || 0) * 2;
    const membersScore = (members || 0) * 10;
    return Math.round((topicsScore + postsScore + membersScore) / foundedYear);
  };

  const calculatePercentageChange = (current: number, previous: number): number => {
    if (previous === 0) return 0;
    return Number(((current - previous) / previous * 100).toFixed(2));
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      // Get current date and date 30 days ago
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

      // Fetch current forums data
      const { data: forums, error: forumsError } = await supabase
        .from('forums')
        .select('*');

      if (forumsError) throw forumsError;
      setAllForums(forums || []);

      // Fetch current categories data
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select('*');

      if (categoriesError) throw categoriesError;

      // Fetch historical forum stats
      const { data: historicalForumStats, error: historicalForumError } = await supabase
        .from('forum_stats_history')
        .select('*')
        .gte('recorded_at', thirtyDaysAgo.toISOString())
        .order('recorded_at', { ascending: false });

      if (historicalForumError) throw historicalForumError;

      // Fetch historical category stats
      const { data: historicalCategoryStats, error: historicalCategoryError } = await supabase
        .from('category_stats_history')
        .select('*')
        .gte('recorded_at', thirtyDaysAgo.toISOString())
        .order('recorded_at', { ascending: false });

      if (historicalCategoryError) throw historicalCategoryError;

      // Calculate current stats
      const totalForums = forums?.length || 0;
      const activeForums = forums?.filter(f => f.is_active).length || 0;
      const activePercentage = Math.round((activeForums / totalForums) * 100);
      const totalPosts = forums?.reduce((sum, forum) => sum + (forum.posts_count || 0), 0) || 0;

      // Calculate historical stats (from 30 days ago)
      const oldForumStats = historicalForumStats?.[historicalForumStats.length - 1] || null;
      const oldCategoryStats = historicalCategoryStats?.[historicalCategoryStats.length - 1] || null;

      // Calculate monthly changes
      const monthlyChanges = {
        forums: calculatePercentageChange(totalForums, oldForumStats ? forums?.length || 0 : totalForums),
        categories: calculatePercentageChange(categories?.length || 0, oldCategoryStats ? categories?.length || 0 : categories?.length || 0),
        posts: calculatePercentageChange(totalPosts, oldForumStats ? oldForumStats.posts_count : totalPosts),
        active: calculatePercentageChange(activePercentage, oldForumStats ? Math.round((activeForums / totalForums) * 100) : activePercentage)
      };

      // Calculate top forums by score
      const topForums = forums
        ?.map(forum => ({
          name: forum.name,
          score: calculateForumScore(
            forum.topics_count,
            forum.posts_count,
            forum.members_count,
            forum.founded_year
          )
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      // Calculate category distribution
      const categoryDistribution = categories?.map(category => ({
        name: category.name,
        count: forums?.filter(f => f.category === category.name).length || 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

      setStats({
        totalForums,
        totalActiveForums: activeForums,
        totalCategories: categories?.length || 0,
        totalPosts,
        activeForumPercentage: activePercentage,
        monthlyChanges,
        topForums: topForums || [],
        categoryDistribution: categoryDistribution || []
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatChange = (change: number): string => {
    return change > 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`;
  };

  const inactiveForums = Math.max(0, stats.totalForums - stats.totalActiveForums);

  const statCards: Array<{
    title: string;
    value: string;
    subValue?: string;
    change: string;
    icon: typeof Users;
    color: string;
  }> = [
    {
      title: 'Toplam Forum',
      value: stats.totalForums.toLocaleString('tr-TR'),
      subValue: `${stats.totalActiveForums.toLocaleString('tr-TR')} aktif · ${inactiveForums.toLocaleString('tr-TR')} pasif`,
      change: formatChange(stats.monthlyChanges.forums),
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: 'Toplam Kategori',
      value: stats.totalCategories.toString(),
      change: formatChange(stats.monthlyChanges.categories),
      icon: BarChart3,
      color: 'bg-purple-500',
    },
    {
      title: 'Toplam İleti',
      value: formatNumber(stats.totalPosts),
      change: formatChange(stats.monthlyChanges.posts),
      icon: MessageCircle,
      color: 'bg-green-500',
    },
    {
      title: 'Aktif Forum Oranı',
      value: `${stats.activeForumPercentage}%`,
      change: formatChange(stats.monthlyChanges.active),
      icon: TrendingUp,
      color: 'bg-amber-500',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-8">Dashboard</h2>

      <ScrapeHealthPanel forums={allForums} />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.title} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-semibold mt-1 dark:text-white">{stat.value}</p>
                  {stat.subValue ? (
                    <p className="mt-1 text-sm font-medium text-green-600 dark:text-green-400">
                      {stat.subValue}
                    </p>
                  ) : null}
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-sm font-medium ${
                  parseFloat(stat.change) >= 0 ? 'text-green-500' : 'text-red-500'
                }`}>
                  {stat.change}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                  geçen aydan
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            En Aktif Forumlar
          </h3>
          <div className="space-y-4">
            {stats.topForums.map((forum, index) => (
              <div key={forum.name} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">#{index + 1}</span>
                  <span className="font-medium dark:text-white">{forum.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Skor:</span>
                  <span className="font-medium dark:text-white">{formatNumber(forum.score)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            Kategori Dağılımı
          </h3>
          <div className="space-y-4">
            {stats.categoryDistribution.map((category) => (
              <div key={category.name} className="flex items-center justify-between">
                <span className="font-medium dark:text-white">{category.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Forum Sayısı:</span>
                  <span className="font-medium dark:text-white">{category.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;